import { getTransactionsV2 } from 'admin/actions/transactions';
import { TransactionState } from 'admin/reducers/transaction';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { updateAccountAttribute } from '../../../actions/account';
import { openAlertModal } from '../../../actions/alert-modal';
import {
  getCustomer,
  getUserAttributes,
  getUserAttributesSchema,
  getUserPrivateMoneys,
} from '../../../actions/customers';
import { useAppDispatch, useAppSelector } from '../../../hooks/useApp';
import { components } from '../../../schema/docs/api/admin';
import { formatUUID, isEmptyObj, toQueryParams } from '../../../utils';
import CopyToClipboard from '../../atoms/CopyToClipboard';
import Grid from '../../atoms/Grid';
import Headings from '../../atoms/Headings';
import Input from '../../atoms/Input';
import Label from '../../atoms/Label';
import Loader from '../../atoms/Loader';
import Select from '../../atoms/Select';
import Accordion from '../../molecules/Accordion';
import ApiResults from '../../molecules/ApiResults';
import BreadCrumb from '../../molecules/BreadCrumb';
import EditableTableCell from '../../molecules/EditableTableCell';
import SeqPager from '../../organisms/SeqPager';
import AccountStatus from './AccountStatus';
import IdentificationStatus from './IdentificationStatus';
import UserAttributes from './UserAttributes';
import UserStatus from './UserStatus';
import UserTransactions from './UserTransactions';
import WithdrawalCustomer from './WithDrawalCustomer';

const FormWrapper = styled.div``;

const FormGrid = styled(Grid.Container)`
  margin-bottom: 12px;

  ${Label} {
    align-self: center;
    margin: 0;
  }
`;

const UserTransactionWrapper = styled.div`
  margin-bottom: 12px;
`;

const CopyTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LoaderWrapper = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const crumbs = (id?: string) => [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: 'お客様管理', href: '/customers', isCurrentPage: false },
  { value: `お客様詳細: ${formatUUID(id) || ''}`, href: '', isCurrentPage: true },
];

type PrivateMoney = components['schemas']['PrivateMoney'];
type PrivateMoneyOption = {
  label: string;
  value: PrivateMoney;
};

type Customer = components['schemas']['Customer'];
type Transaction = TransactionState['transactionsV2'];

type ApiMessage = {
  text: string;
  isError: boolean;
};

const CustomerDetail = ({ match, location }: RouteComponentProps<{ id?: string }>) => {
  const customerId = match.params.id;
  const user = useAppSelector((state) => state.user);
  const isServiceUser = user.isServiceUser();
  const selectedPrivateMoney = useAppSelector(selectUserPrivateMoney);

  // only when user navigates from /transactions/:id?private_money_id=xxx, used for initial selection of money selector
  const transactionPrivateMoneyId = new URLSearchParams(location?.search).get('private_money_id');

  const [privateMoneyOption, setPrivateMoneyOptions] = useState<PrivateMoneyOption[]>([]);
  const [selectedPrivateMoneyOption, setSelectedPrivateMoneyOption] = useState<PrivateMoneyOption>({
    label: '',
    value: {} as PrivateMoney,
  });
  const [customer, setCustomer] = useState<Customer>({} as Customer);
  const [externalId, setExternalId] = useState<string>();
  const [apiMessages, setApiMessages] = useState<ApiMessage[]>([]);
  const [userAttributesSchema, setUserAttributesSchema] = useState<Record<string, any>>({});
  const [userAttributes, setUserAttributes] = useState<Record<string, any>>({});
  const [userTransactions, setUserTransactions] = useState<Transaction>({} as Transaction);

  const dispatch = useAppDispatch();

  const fetchCustomer = useCallback(() => {
    if (customerId && selectedPrivateMoneyOption.value?.id) {
      getCustomer(
        customerId,
        toQueryParams({
          private_money_id: selectedPrivateMoneyOption.value.id,
        })
      ).then((c) => {
        setCustomer(c);
        setExternalId(c?.external_id);
      });
    }
  }, [customerId, selectedPrivateMoneyOption.value?.id]);

  const customerDetails = [
    {
      label: 'お客様ID',
      value: (
        <CopyTextWrapper>
          <span>{customer.id}</span>
          <CopyToClipboard text={customer.id} />
        </CopyTextWrapper>
      ),
    },
    { label: 'ニックネーム', value: customer.name },
    { label: '携帯電話番号', value: customer.tel },
    { label: 'メールアドレス', value: customer.email },
    {
      label: '会員登録日時',
      value: customer.registered_at && moment(customer.registered_at).format('YYYY/MM/DD HH:mm:ss'),
    },
    {
      label: 'マイナンバー認証',
      value: (
        <IdentificationStatus
          customerId={customerId}
          privateMoneyId={selectedPrivateMoneyOption.value?.id}
          identification={customer.individual_number_identification}
          setApiMessages={setApiMessages}
          onUpdated={() => fetchCustomer()}
        />
      ),
    },
  ];

  const fetchUserAttributesSchema = useCallback(() => {
    if (customerId && selectedPrivateMoneyOption.value?.id) {
      getUserAttributesSchema(selectedPrivateMoneyOption.value.id)
        .then((res) => {
          setUserAttributesSchema(res as Record<string, unknown>);
        })
        .catch(() => {
          setUserAttributesSchema({});
        });
    }
  }, [customerId, selectedPrivateMoneyOption.value?.id]);

  const fetchUserAttributes = useCallback(() => {
    if (customerId && selectedPrivateMoneyOption.value?.id) {
      getUserAttributes(customerId, selectedPrivateMoneyOption.value.id)
        .then((res) => {
          setUserAttributes(res as Record<string, unknown>);
        })
        .catch((err) => {
          setUserAttributes({});
          console.error(err);
        });
    }
  }, [customerId, selectedPrivateMoneyOption.value?.id]);

  const fetchTransactions = useCallback(
    async (paginatedQuery?: string) => {
      if (customerId && selectedPrivateMoneyOption.value?.id) {
        const query =
          paginatedQuery ||
          toQueryParams({
            customer_id: customerId,
            private_money: selectedPrivateMoneyOption.value.id,
          });

        try {
          const res = await dispatch(getTransactionsV2(query));
          setUserTransactions(res.payload);
        } catch (err) {
          console.error(err);
        }
      }
    },
    [customerId, selectedPrivateMoneyOption.value?.id, dispatch]
  );

  const onChangePrivateMoneyOption = (option: PrivateMoneyOption) => {
    if (selectedPrivateMoneyOption !== option) {
      setSelectedPrivateMoneyOption(option);
    }
  };

  const onUpdateExternalId = () => {
    if (customer.account_uuid) {
      updateAccountAttribute(customer.account_uuid, { external_id: externalId })
        .then(() => fetchCustomer())
        .catch((errorRes) => {
          errorRes.then(console.error);

          dispatch(
            openAlertModal({
              body: 'エラーが発生しました。管理者へお問合せくださ',
              confirmText: 'OK',
            })
          );
          setExternalId(customer?.external_id);
        });
    }
  };

  const onCancelUpdateExternalId = () => {
    setExternalId(customer?.external_id);
  };

  useEffect(() => {
    if (customerId) {
      getUserPrivateMoneys(customerId).then((privateMoneys) => {
        const options: PrivateMoneyOption[] = privateMoneys
          .filter((money) => isServiceUser || money.organization?.code === user.organization?.code)
          .map((money) => ({ label: money?.name || '', value: money }));

        setPrivateMoneyOptions(options);

        if (options.length > 0) {
          if (transactionPrivateMoneyId) {
            const matchingOption = options.find(
              (option) => option.value.id === transactionPrivateMoneyId
            );
            if (matchingOption) {
              setSelectedPrivateMoneyOption(matchingOption);
              return;
            }
          }

          if (selectedPrivateMoney?.id) {
            const matchingOption = options.find(
              (option) => option.value.id === selectedPrivateMoney.id
            );
            if (matchingOption) {
              setSelectedPrivateMoneyOption(matchingOption);
              return;
            }
          }

          setSelectedPrivateMoneyOption(options[0]);
        }
      });
    }
  }, [customerId, isServiceUser, user, transactionPrivateMoneyId, selectedPrivateMoney]);

  useEffect(() => {
    fetchCustomer();
    fetchTransactions();
    fetchUserAttributesSchema();
    fetchUserAttributes();
    setApiMessages([]);
  }, [
    fetchCustomer,
    fetchTransactions,
    fetchUserAttributes,
    fetchUserAttributesSchema,
    setSelectedPrivateMoneyOption,
  ]);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={crumbs(customer.id)} />
      <ApiResults messages={apiMessages} />
      <Headings.h5>お客様詳細</Headings.h5>
      {Object.keys(customer).length !== 0 ? (
        <>
          <FormGrid gridSize={12}>
            <Grid.Item size={2}>
              <Label>マネー名</Label>
            </Grid.Item>
            <Grid.Item size={4}>
              <Select
                fullWidth
                options={privateMoneyOption}
                value={selectedPrivateMoneyOption}
                onChange={onChangePrivateMoneyOption}
              />
            </Grid.Item>
            <Grid.Item startColumn={11} size={2} justifyContent="end">
              {!!customerId && isServiceUser && (
                <WithdrawalCustomer
                  customerId={customerId}
                  onDeleted={() => {
                    window.location.reload();
                  }}
                  setApiMessages={setApiMessages}
                />
              )}
            </Grid.Item>
          </FormGrid>

          <Headings.h5>登録情報</Headings.h5>
          <FormGrid gridSize={12}>
            <Grid.Item size={2}>
              <Label>状態</Label>
            </Grid.Item>
            <Grid.Item size={4}>
              <AccountStatus
                customer={customer}
                setApiMessages={setApiMessages}
                onAccountStatusUpdated={() => fetchCustomer()}
              />
            </Grid.Item>

            <Grid.Divider />

            {customerDetails.map((detail) => (
              <React.Fragment key={detail.label}>
                <Grid.Item size={2}>
                  <Label>{detail.label}</Label>
                </Grid.Item>
                <Grid.Item size={10}>
                  <Label>{detail.value}</Label>
                </Grid.Item>

                <Grid.Divider />
              </React.Fragment>
            ))}

            <Grid.Item size={2}>
              <Label>外部ID</Label>
            </Grid.Item>
            <Grid.Item size={4}>
              <EditableTableCell
                onUpdate={onUpdateExternalId}
                onCancel={onCancelUpdateExternalId}
                renderInput={() => (
                  <Input
                    type="text"
                    fullWidth
                    syncValue={externalId}
                    onChange={({ target: { value } }) => {
                      setExternalId(value);
                    }}
                    inputStyles={{
                      flex: '100%',
                    }}
                    maxLength={50}
                  />
                )}
                renderValue={() => <span>{externalId}</span>}
              />
            </Grid.Item>

            <Grid.Divider />
          </FormGrid>

          <Accordion
            renderHeader={() => <span>アンケート情報</span>}
            renderBody={() =>
              !isEmptyObj(userAttributesSchema) && !isEmptyObj(userAttributes) ? (
                <UserAttributes
                  userAttributesSchema={userAttributesSchema}
                  userAttributes={userAttributes}
                />
              ) : null
            }
          />

          <UserStatus
            customer={customer}
            selectedPrivateMoney={selectedPrivateMoneyOption?.value}
          />

          <Headings.h5>取引履歴</Headings.h5>
          <UserTransactionWrapper>
            <UserTransactions transactions={userTransactions?.rows} />
          </UserTransactionWrapper>
          <SeqPager
            prev={userTransactions?.prev}
            next={userTransactions?.next}
            onChangePage={fetchTransactions}
          />
        </>
      ) : (
        <LoaderWrapper>
          <Loader type="Oval" width={50} height={50} />
        </LoaderWrapper>
      )}
    </FormWrapper>
  );
};

export default CustomerDetail;
